<template>
  <v-row>
    <v-col>
      <v-data-table
        :headers="headers"
        :items="refunds"
        :server-items-length="totalRefunds"
        :loading="refundsLoading"
        hide-default-footer
        :multi-sort="false"
        :must-sort="true"
        @page-count="pageCount = $event"
        :page.sync="page"
        :options.sync="options"
        :items-per-page="itemsPerPage"
        :footer-props="{ showFirstLastPage: true }"
        class="ic-table elevation-0 ic-table--heavy-headers"
      >
        <template v-slot:top>
          <v-container fluid class="white header-container">
            <v-row align="center" dense justify="center">
              <v-col cols="12" md="2" class="v-toolbar__title">
                {{ $t("pages.refund.list.title") }}
              </v-col>
              <v-col cols="12" md="10">
                <div class="d-flex flex-wrap justify-end toolbar-items">
                  <v-text-field
                    class="toolbar-item"
                    v-model="dynamicSearch"
                    append-icon="$icons_system_search"
                    :label="$t('misc.search')"
                    single-line
                    clearable
                    dense
                    outlined
                    hide-details
                    style="max-width: 184px;"
                  ></v-text-field>
                  <v-select class="toolbar-item date-type-picker"
                            :items="dateTypes"
                            hide-details
                            outlined
                            dense
                            value="refundDate"
                            v-model="dateType"
                            style="max-width: 224px;"
                  ></v-select>
                  <div class="toolbar-item refund-date-picker" style="max-width: 284px;">
                    <v-icon class="date-picker-icon-calendar">$icons_product_calendar</v-icon>
                    <date-picker
                      :label="$t('pages.refund.list.startDate')"
                      :locale="selectedLanguage"
                      :displayFormat="$t('pages.refund.list.dateFormat')"
                      v-model="startDate"
                      :max="endDate"
                    ></date-picker>
                    <v-icon class="date-picker-icon-arrow">$icons_system_arrow</v-icon>
                    <date-picker
                      :label="$t('pages.refund.list.endDate')"
                      :locale="selectedLanguage"
                      :displayFormat="$t('pages.refund.list.dateFormat')"
                      v-model="endDate"
                      :min="startDate"
                    ></date-picker>
                  </div>
                  <v-btn
                    color="primary"
                    :loading="reportGenerating"
                    :disabled="disableDownload"
                    @click="downloadReport()"
                    class="toolbar-item header-list-button"
                  >
                    <span>{{ $t("pages.refund.list.download") }}</span>
                  </v-btn>
                  <div class="toolbar-item"></div>
                  <create-refund-dialog class="toolbar-item" v-if="hasAtLeastOneAccess(['fleetAdmin', 'paymentManager'])" :charge="charge" :refundState="refundState" :loadingCharge="loadingCharge" @searchByDispatchId="searchByDispatchId" @createChargeRefund="createChargeRefund"></create-refund-dialog>
                </div>
              </v-col>
            </v-row>
          </v-container>
          <div class="header-separator"></div>
        </template>
        <template v-slot:header.date="{ header }">
          <span class="custom-table-header">{{ header.text }}</span>
        </template>
        <template v-slot:header.payout.arrivalDate="{ header }">
          <span class="custom-table-header">{{ header.text }}</span>
        </template>
        <template v-slot:header.card.brand="{ header }">
          <span class="custom-table-header">{{ header.text }}</span>
        </template>
        <template v-slot:header.booking.dispatchId="{ header }">
          <span class="custom-table-header">{{ header.text }}</span>
        </template>
        <template v-slot:header.applicationFee="{ header }">
          <span class="custom-table-header">{{ header.text }}</span>
        </template>

        <template v-slot:header.booking.amount="{ header }">
          <span class="custom-table-header">{{ header.text }}</span>
        </template>
        <template v-slot:header.refundAmount="{ header }">
          <span class="custom-table-header">{{ header.text }}</span>
        </template>
        <template v-slot:header.refundDate="{ header }">
          <span class="custom-table-header">{{ header.text }}</span>
        </template>
        <template v-slot:header.remainingAmount="{ header }">
          <span class="custom-table-header">{{ header.text }}</span>
        </template>

        <template v-slot:item.date="{ item }">
          <span>{{ formatDate(item.date) }}</span>
        </template>
        <template v-slot:item.refundDate="{ item }">
          <span>{{ formatDate(item.refundDate) }}</span>
        </template>
        <template v-slot:item.payout.arrivalDate="{ item }">
          <span>{{ formatDate(item.payout && item.payout.arrivalDate) }}</span>
        </template>

        <template v-slot:item.remainingAmount="{ item }">
          <span>{{ (item.booking.amount - item.refundAmount).toFixed(2) }}</span>
        </template>

        <template v-slot:item.card.brand="{ item }">
              <span>
                <v-icon>{{ creditCardIcon(item.card.brand) }}</v-icon>
              </span>
        </template>

      </v-data-table>
      <div class="text-center pt-8">
        <v-pagination v-model="page" :length="pageCount" total-visible="10"></v-pagination>
      </div>
    </v-col>
  </v-row>
</template>

<script>
import debounce from 'debounce';
import moment from 'moment';
import dayjs from '../plugins/dayjs';
import DatePicker from './DatePicker.vue';
import CreateRefundDialog from './CreateRefundDialog.vue';
import { creditCardIcon } from '../mixins/creditCardIcons.mixins';
import { accesses } from '../mixins/authentication.mixins';

export default {
  name: 'refundList',
  components: { CreateRefundDialog, DatePicker },
  mixins: [
    creditCardIcon,
    accesses,
  ],
  props: {
    refundsLoading: {
      type: Boolean,
      required: true,
    },
    charge: {
      type: Object,
      required: false,
    },
    refundState: {
      type: String,
      required: false,
    },
    loadingCharge: {
      type: Boolean,
      required: true,
    },
    refunds: {
      type: Array,
      required: true,
    },
    totalRefunds: {
      type: Number,
      required: true,
    },
    selectedLanguage: {
      type: String,
      required: true,
    },
    reportGenerating: {
      type: Boolean,
      required: true,
    },
  },
  created() {
    this.debouncedLoad = debounce(this.load, 500);
  },
  data() {
    return {
      screenName: 'refund-list',
      options: {
        sortBy: ['refundDate'], sortDesc: [true],
      },
      dialogs: {},
      page: 1,
      pageCount: 0,
      itemsPerPage: 10,
      loading: false,
      dateType: 'refundDate',
      startDate: dayjs()
        .subtract(31, 'days')
        .format('YYYY-MM-DD'),
      endDate: dayjs().format('YYYY-MM-DD'),
      dynamicSearch: '',
    };
  },
  watch: {
    options: {
      handler() {
        this.load();
      },
      deep: true,
    },
    totalRefunds() {
      this.options.pageCount = Math.round(this.totalRefunds / this.itemsPerPage) + 1;
    },
    startDate() {
      this.load();
    },
    endDate() {
      this.load();
    },
    dateType() {
      this.load();
    },
    dynamicSearch() {
      this.page = 1;
      this.debouncedLoad();
    },
  },
  computed: {
    dateTypes() {
      return [
        { text: this.$t('pages.refund.filters.refundDate'), value: 'refundDate' },
        { text: this.$t('pages.refund.filters.transactionDate'), value: 'transactionDate' },
        { text: this.$t('pages.refund.filters.payoutDate'), value: 'payoutDate' },
      ];
    },
    disableDownload() {
      return false;
    },
    startDateForRequest() {
      return `${this.startDate}T00:00:00Z`;
    },
    endDateForRequest() {
      return `${this.endDate}T23:59:59Z`;
    },
    headers() {
      return [
        {
          text: this.$t('pages.dispute.list.bookingChannelName'),
          align: 'left',
          sortable: true,
          value: 'booking.bookingChannelName',
        },
        {
          text: this.$t('pages.refund.list.refundDate'),
          align: 'left',
          sortable: true,
          value: 'refundDate',
        },
        {
          text: this.$t('pages.refund.list.transactionDate'),
          align: 'left',
          sortable: true,
          value: 'date',
        },
        {
          text: this.$t('pages.refund.list.bookingId'),
          align: 'left',
          sortable: true,
          value: 'booking.dispatchId',
        },
        {
          text: this.$t('pages.refund.list.cardBrand'),
          align: 'left',
          sortable: true,
          value: 'card.brand',
        },
        {
          text: this.$t('pages.refund.list.fareAmount'),
          align: 'left',
          sortable: true,
          value: 'booking.amount',
        },
        {
          text: this.$t('pages.refund.list.payoutDate'),
          align: 'left',
          sortable: true,
          value: 'payout.arrivalDate',
        },
        {
          text: this.$t('pages.refund.list.refundAmount'),
          align: 'left',
          sortable: true,
          value: 'refundAmount',
        },
        {
          text: this.$t('pages.refund.list.remainingAmount'),
          align: 'left',
          sortable: false,
          value: 'remainingAmount',
        },
      ];
    },
  },
  methods: {
    async load() {
      if (moment(this.endDate).diff(moment(this.startDate), 'days') > 31) {
        this.$store.dispatch('globalMessages/addError',
          { message: 'lists.dateRangeError' });
        return;
      }
      const { page, itemsPerPage, sortBy, sortDesc } = this.options;
      const sort = sortBy[0] || 'refundDate';
      this.$emit('loadData', {
        page: page - 1,
        startDate: this.startDateForRequest,
        endDate: this.endDateForRequest,
        dateType: this.dateType,
        maxPerPage: itemsPerPage,
        sort: sortDesc[0] ? `-${sort}` : sort,
        dynamicSearch: this.dynamicSearch,
      });
    },
    downloadReport() {
      const { sortBy, sortDesc } = this.options;
      const sort = sortBy[0] || 'refundDate';
      this.$emit('downloadReport', {
        startDate: this.startDateForRequest,
        endDate: this.endDateForRequest,
        dateType: this.dateType,
        sort: sortDesc[0] ? `-${sort}` : sort,
      });
    },
    createChargeRefund(e) {
      this.$emit('createChargeRefund', e);
    },
    searchByDispatchId(e) {
      this.$emit('searchByDispatchId', e);
    },
    formatDate(date) {
      if (!date) return null;
      return dayjs(date).utc().format('YYYY-MM-DD HH:mm');
    },
  },
};
</script>

<style lang="scss">
.toolbar-items {
  .toolbar-item:not(:first-child) {
    margin-left: 8px;
    margin-bottom: 8px;
  }
}
.refund-date-picker {
  display: flex;
  flex-flow: row;
  border: 1px #a1a1a1 solid;
  border-radius: 5px;
  padding: 3px 12px;
  max-height: 40px;
  align-items: center;
}

.mobile-title-refund {
  display: flex;
  justify-content: space-between;

  .header-list-button {
    padding: 0 !important;
    min-width: 45px !important;
  }
}

.refund-header-menu {
  display: flex;
  flex-flow: row;
  align-items: center;
}

.date-picker-icon-arrow {
  min-width: 20px;
  min-height: 20px;
}

.date-picker-icon-calendar {
  width: 30px;
  height: 30px;
  min-width: 20px;
  min-height: 20px;
}

@media only screen and (max-width: 1273px) {
  .date-picker-icon-calendar {
    min-width: 20px;
    min-height: 20px;
    width: 20px;
    height: 20px;
  }
}

@media only screen and (max-width: 959px) {
  .refund-header-menu-input {
    padding-top: 10px !important;
    padding-bottom: 10px !important;
  }
}
</style>
